import React from 'react';
import { motion } from 'framer-motion';
import { FaAward, FaLeaf, FaHeart, FaGlobe } from 'react-icons/fa';
import Newsletter from '../components/Newsletter/Newsletter';
import './About.css';

const team = [
  { name: "Alexander Montague", role: "General Manager", img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=300&q=80" },
  { name: "Isabella Chen", role: "Head of Guest Experience", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&q=80" },
  { name: "Marcus Williams", role: "Executive Chef", img: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=300&q=80" },
  { name: "Sophie Laurent", role: "Spa Director", img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=300&q=80" },
];

const milestones = [
  { year: "1995", title: "Founded", desc: "Grand Luxe opened its doors with 50 rooms and a vision to redefine luxury." },
  { year: "2003", title: "Expansion", desc: "Added 200 suites and the award-winning Lumière restaurant." },
  { year: "2010", title: "Spa Launch", desc: "Opened the 5,000 sqm Serenity Spa, recognized globally." },
  { year: "2018", title: "Sustainability", desc: "Became the first carbon-neutral luxury hotel in the region." },
  { year: "2024", title: "Reimagined", desc: "Completed $50M renovation — luxury reimagined for a new era." },
];

export default function About() {
  return (
    <main>
      <div className="page-banner">
        <div className="page-banner-content">
          <span className="section-badge">Our Story</span>
          <h1>About Grand Luxe</h1>
          <p>Three decades of unparalleled luxury hospitality</p>
        </div>
      </div>

      <section className="section about-intro">
        <div className="container about-intro-inner">
          <motion.div
            className="about-image-wrap"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img src="https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=800&q=80" alt="Hotel" className="about-main-img" />
            <img src="https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400&q=80" alt="Pool" className="about-accent-img" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="section-badge">Est. 1995</span>
            <h2 className="section-title" style={{ textAlign: 'left', marginBottom: '8px' }}>A Legacy of Luxury</h2>
            <div className="gold-line" style={{ margin: '12px 0 24px' }} />
            <p style={{ color: 'var(--text-light)', lineHeight: 1.9, marginBottom: '20px' }}>
              Since 1995, Grand Luxe Hotel & Spa has been a sanctuary for travelers seeking the extraordinary.
              Founded on the belief that true luxury is a deeply personal experience, we have spent three decades
              crafting moments that transcend the ordinary.
            </p>
            <p style={{ color: 'var(--text-light)', lineHeight: 1.9, marginBottom: '32px' }}>
              Every detail — from the hand-embroidered linens to the single-origin coffee in our suites —
              reflects our unwavering commitment to excellence. We don't just offer rooms; we create memories.
            </p>

            <div className="about-values">
              {[
                { icon: FaAward, title: "Excellence", desc: "25+ international awards" },
                { icon: FaLeaf, title: "Sustainability", desc: "Carbon-neutral since 2018" },
                { icon: FaHeart, title: "Heart", desc: "500+ passionate professionals" },
                { icon: FaGlobe, title: "Global", desc: "Guests from 80+ countries" },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="about-value-item">
                  <Icon />
                  <div>
                    <strong>{title}</strong>
                    <span>{desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section timeline-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Our Journey</span>
            <h2 className="section-title">Milestones & Heritage</h2>
            <div className="gold-line" />
          </div>
          <div className="timeline">
            {milestones.map((m, i) => (
              <motion.div
                key={m.year}
                className={`timeline-item ${i % 2 === 0 ? 'left' : 'right'}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <div className="timeline-content">
                  <span className="timeline-year">{m.year}</span>
                  <h4>{m.title}</h4>
                  <p>{m.desc}</p>
                </div>
                <div className="timeline-dot" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section team-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Leadership</span>
            <h2 className="section-title">Meet Our Team</h2>
            <div className="gold-line" />
            <p className="section-subtitle">The passionate individuals who make Grand Luxe extraordinary.</p>
          </div>
          <div className="team-grid">
            {team.map((member, i) => (
              <motion.div
                key={member.name}
                className="team-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
              >
                <div className="team-card-img">
                  <img src={member.img} alt={member.name} />
                </div>
                <h4>{member.name}</h4>
                <span>{member.role}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Newsletter />
    </main>
  );
}
